import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, FolderTree, Hash, Type, AlignLeft, ArrowUpDown, Tag } from 'lucide-react';

/**
 * CategoryForm component - Add/edit job category modal form
 * Handles both creating new categories and editing existing ones
 */
const CategoryForm = ({ 
  isOpen, 
  onClose, 
  category = null, // null for add, category object for edit
  categories = [], 
  onSave,
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    category_name: '',
    parent_id: '',
    description: '',
    sort_order: 0,
    qb_class: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!category;

  // Initialize form data when category prop changes
  useEffect(() => {
    if (category) {
      setFormData({
        category_name: category.category_name || '',
        parent_id: category.parent_id || '',
        description: category.description || '',
        sort_order: category.sort_order || 0,
        qb_class: category.qb_class || ''
      });
    } else {
      setFormData({
        category_name: '',
        parent_id: '',
        description: '',
        sort_order: 0,
        qb_class: ''
      });
    }
    setErrors({});
  }, [category, isOpen]);

  // Get available parent categories (exclude current category and its descendants for edit)
  const getAvailableParents = () => {
    let availableParents = categories.filter(cat => cat.is_active);
    
    if (isEditing && category) {
      // Exclude current category and its descendants
      availableParents = availableParents.filter(cat => {
        if (cat.id === category.id) return false;
        if (cat.full_path.startsWith(category.full_path + ' >')) return false;
        return true;
      });
    }
    
    // Sort by full path for better hierarchy display
    return availableParents.sort((a, b) => a.full_path.localeCompare(b.full_path));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.category_name.trim()) {
      newErrors.category_name = 'Category name is required';
    } else if (formData.category_name.length < 2) {
      newErrors.category_name = 'Category name must be at least 2 characters';
    } else if (formData.category_name.length > 100) {
      newErrors.category_name = 'Category name must be less than 100 characters';
    }

    // Check for duplicate names at the same level
    const duplicateCheck = categories.find(cat => 
      cat.category_name.toLowerCase() === formData.category_name.toLowerCase() &&
      cat.parent_id === (formData.parent_id || null) &&
      (!isEditing || cat.id !== category.id)
    );
    
    if (duplicateCheck) {
      newErrors.category_name = 'A category with this name already exists at this level';
    }

    if (formData.parent_id) {
      const parentCategory = categories.find(cat => cat.id === formData.parent_id);
      if (parentCategory && parentCategory.category_level >= 3) {
        newErrors.parent_id = 'Maximum category depth of 3 levels exceeded';
      }
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    if (isNaN(formData.sort_order) || formData.sort_order < 0) {
      newErrors.sort_order = 'Sort order must be a non-negative number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave({
        ...formData,
        category_name: formData.category_name.trim(),
        description: formData.description.trim() || null,
        sort_order: Number(formData.sort_order),
        qb_class: formData.qb_class || null
      });
      onClose();
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  if (!isOpen) return null;

  const selectedParent = formData.parent_id ? 
    categories.find(cat => cat.id === formData.parent_id) : null;
  
  const newLevel = selectedParent ? selectedParent.category_level + 1 : 1;
  const newPath = selectedParent ? 
    `${selectedParent.full_path} > ${formData.category_name}` : 
    formData.category_name;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-start justify-center z-50 pt-8 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden m-4 flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
          <div>
            <h2 className="text-xl font-bold text-white">
              {isEditing ? 'Edit Job Category' : 'Add New Job Category'}
            </h2>
            <p className="text-sm text-white/90 mt-1">
              {isEditing ? 'Update category details' : 'Create a new job category for technician assignment'}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-white hover:text-gray-200 transition-colors duration-200 disabled:opacity-50"
            aria-label="Close form"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="p-6 overflow-y-auto flex-1">
            <div className="space-y-6">
              {/* Category Name */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Type className="w-4 h-4 mr-2" />
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.category_name}
                  onChange={(e) => handleChange('category_name', e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors ${
                    errors.category_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter category name (e.g., 'Wasp Nest Removal')"
                  maxLength={100}
                  disabled={isSubmitting}
                />
                {errors.category_name && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.category_name}
                  </p>
                )}
              </div>

              {/* Parent Category */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <FolderTree className="w-4 h-4 mr-2" />
                  Parent Category (Optional)
                </label>
                <select
                  value={formData.parent_id}
                  onChange={(e) => handleChange('parent_id', e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors ${
                    errors.parent_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">No parent (top level category)</option>
                  {getAvailableParents().map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.full_path} (Level {cat.category_level})
                    </option>
                  ))}
                </select>
                {errors.parent_id && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.parent_id}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <AlignLeft className="w-4 h-4 mr-2" />
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors resize-vertical ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Describe what this job category involves (e.g., 'Removal of wasp nests from various locations including lofts, gardens, and commercial properties')"
                  rows={3}
                  maxLength={500}
                  disabled={isSubmitting}
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.description ? (
                    <p className="text-red-500 text-sm flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.description}
                    </p>
                  ) : (
                    <span></span>
                  )}
                  <span className="text-gray-400 text-xs">
                    {formData.description.length}/500
                  </span>
                </div>
              </div>

              {/* QuickBooks Class */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Tag className="w-4 h-4 mr-2" />
                  QuickBooks Class (Optional)
                </label>
                <select
                  value={formData.qb_class}
                  onChange={(e) => handleChange('qb_class', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
                  disabled={isSubmitting}
                >
                  <option value="">No QB class mapped</option>
                  <optgroup label="Residential">
                    <option value="Residential">Residential</option>
                    <option value="Residential:Residential Job">Residential:Residential Job</option>
                    <option value="Residential:Residential Job inc Subcontractor">Residential:Residential Job inc Subcontractor</option>
                    <option value="Residential:Residential Contract">Residential:Residential Contract</option>
                  </optgroup>
                  <optgroup label="Commercial">
                    <option value="Commercial">Commercial</option>
                    <option value="Commercial:Commercial Job">Commercial:Commercial Job</option>
                    <option value="Commercial:Commercial Job inc Subcontractor">Commercial:Commercial Job inc Subcontractor</option>
                    <option value="Commercial:Commercial Contract">Commercial:Commercial Contract</option>
                    <option value="Commercial:Property Management Job">Commercial:Property Management Job</option>
                    <option value="Commercial:Property Management Contract">Commercial:Property Management Contract</option>
                  </optgroup>
                </select>
                <p className="text-gray-500 text-sm mt-1">
                  Maps to the QuickBooks class on invoices created from this job type
                </p>
              </div>

              {/* Sort Order */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  Sort Order
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => handleChange('sort_order', e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors ${
                    errors.sort_order ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0"
                  min="0"
                  step="1"
                  disabled={isSubmitting}
                />
                {errors.sort_order && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.sort_order}
                  </p>
                )}
                <p className="text-gray-500 text-sm mt-1">
                  Lower numbers appear first in lists (0 = highest priority)
                </p>
              </div>

              {/* Preview */}
              {formData.category_name && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">Preview</h4>
                  <div className="space-y-1 text-sm text-blue-700">
                    <p><strong>Full Path:</strong> {newPath}</p>
                    <p><strong>Category Level:</strong> {newLevel}</p>
                    <p><strong>Category Code:</strong> {formData.category_name.toUpperCase().replace(/[^A-Z0-9\s]/g, '').replace(/\s+/g, '_')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 focus:ring-2 focus:ring-gray-200 transition-colors duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.category_name.trim()}
              className="px-6 py-2 bg-pest-blue hover:bg-pest-blue-dark text-white font-medium rounded-lg focus:ring-2 focus:ring-pest-blue focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save size={18} />
                  {isEditing ? 'Update Category' : 'Create Category'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryForm;